import { useEffect, useState } from "react";
import { Eye, Phone, Navigation, Globe, CalendarPlus, BarChart3, Tag, Mail } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { useAuth } from "@/context/AuthContext";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend } from "recharts";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api } from "@/lib/api";

const METRICS = [
  ["view", "Views", Eye, "#FF3B6B"], ["call", "Calls", Phone, "#00E5A3"], ["directions", "Directions", Navigation, "#FFB800"],
  ["website", "Website", Globe, "#60A5FA"], ["trip_add", "Trip saves", CalendarPlus, "#C084FC"], ["deal_claim", "Deal claims", Tag, "#F472B6"],
];

const lastMonths = () => Array.from({ length: 6 }, (_, i) => {
  const d = new Date(); d.setDate(1); d.setMonth(d.getMonth() - i);
  return { value: d.toISOString().slice(0, 7), label: d.toLocaleDateString([], { month: "long", year: "numeric" }) };
});

function ReportToggle() {
  const { user, setUser } = useAuth();
  const toggle = (on) => api.patch("/auth/preferences", { monthly_report: on }).then((r) => setUser(r.data)).catch(() => {});
  return (
    <label className="mt-4 flex items-center gap-3 text-sm text-slate-300">
      <Mail className="h-4 w-4 text-[#00E5A3]" /> Email me a traveler activity report on the 1st of every month
      <Switch data-testid="vendor-monthly-report-toggle" checked={user.monthly_report !== false} onCheckedChange={toggle} />
    </label>
  );
}

export function VendorInsights() {
  const months = lastMonths();
  const [month, setMonth] = useState(months[0].value);
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/vendor/insights", { params: { month } }).then((r) => setData(r.data)).catch(() => setData(null)); }, [month]);

  return (
    <section data-testid="vendor-insights" className="mt-12">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <h2 className="text-2xl font-bold flex items-center gap-2"><BarChart3 className="h-5 w-5 text-[#FFB800]" /> Traveler activity</h2>
        <Select value={month} onValueChange={setMonth}>
          <SelectTrigger data-testid="insights-month-select" className="w-48 rounded-full bg-[#151C2C] border-[#26334D]"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-[#151C2C] border-[#26334D] text-white">
            {months.map((m) => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        {METRICS.map(([k, label, Icon, color]) => (
          <div key={k} className="rounded-2xl bg-[#151C2C] border border-[#26334D] p-4">
            <Icon className="h-4 w-4" style={{ color }} />
            <p data-testid={`insights-total-${k}`} className="text-3xl font-extrabold mt-2">{data?.totals[k] ?? 0}</p>
            <p className="text-xs text-slate-400">{label}</p>
          </div>
        ))}
      </div>
      <div className="mt-4 rounded-2xl bg-[#151C2C] border border-[#26334D] p-4 h-64">
        {data?.daily.length ? (
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={data.daily.map((d) => ({ ...d, day: d.day.slice(8) }))}>
              <XAxis dataKey="day" stroke="#64748B" fontSize={12} />
              <YAxis allowDecimals={false} stroke="#64748B" fontSize={12} />
              <Tooltip contentStyle={{ background: "#0B0F19", border: "1px solid #26334D", borderRadius: 12 }} cursor={{ fill: "#1E293B" }} />
              <Legend />
              {METRICS.slice(0, 3).map(([k, label, , color]) => <Bar key={k} dataKey={k} name={label} stackId="a" fill={color} />)}
            </BarChart>
          </ResponsiveContainer>
        ) : <p data-testid="insights-empty" className="h-full grid place-items-center text-sm text-slate-500">No traveler activity this month yet. Activity appears once your plan is active.</p>}
      </div>
      {data?.deals?.length > 0 && (
        <div data-testid="insights-deals" className="mt-4 rounded-2xl bg-[#151C2C] border border-[#26334D] p-4">
          <h3 className="text-sm font-semibold mb-3 flex items-center gap-2"><Tag className="h-4 w-4 text-[#F472B6]" /> Deal performance</h3>
          {data.deals.map((d) => (
            <div key={`${d.business}-${d.id}`} data-testid={`insights-deal-${d.id}`} className="flex justify-between text-sm py-2 border-t border-[#26334D] first:border-0">
              <span>{d.title} <span className="text-slate-500">· {d.business} · ends {d.ends}{d.ends < new Date().toLocaleDateString("en-CA") ? " (ended)" : ""}</span></span>
              <span className="font-mono-vv text-[#F472B6]">{d.claims} claims</span>
            </div>
          ))}
        </div>
      )}
      <ReportToggle />
      {data?.businesses.length > 1 && (
        <div className="mt-4 rounded-2xl border border-[#26334D] overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-slate-400 text-xs uppercase"><tr><th className="text-left p-3">Business</th>{METRICS.map(([k, l]) => <th key={k} className="p-3 text-right">{l}</th>)}</tr></thead>
            <tbody>{data.businesses.map((b) => (
              <tr key={b.id} data-testid={`insights-row-${b.id}`} className="border-t border-[#26334D]"><td className="p-3">{b.name}</td>{METRICS.map(([k]) => <td key={k} className="p-3 text-right font-mono-vv">{b[k]}</td>)}</tr>
            ))}</tbody>
          </table>
        </div>
      )}
    </section>
  );
}
